/**
 * AuditSection 稽核 (S2.3) — record-derived audit facts only (created /
 * updated / price-hold release with approver + reason / per-line manual
 * override reasons). No fabricated rows or timestamps: line overrides carry
 * no event time of their own, so their timestamp cell renders '-'. The
 * global audit lens (every ReasonRequiredModal write) arrives with
 * governance in W10.
 */

import { Table, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import TableContainer, { renderDash } from '@/components/common/TableContainer';
import { TimestampDisplay } from '@/components/common/TimestampDisplay';
import { SectionHeader } from '@/components/products/ProductDetailPage/sections/SectionHeader';
import type {
  SalesOrder,
  SalesOrderPriceApproval,
  SalesOrderPriceHoldReason,
} from '@/types/api/salesOrder';
import { SO_PRICE_DEVIATION_THRESHOLD_PCT } from '@/types/api/salesOrder';
import { formatPct, holdReasonKey } from '../../components/soFormat';

interface AuditRow {
  key: string;
  /** i18n key for the event cell (interpolated with `values`). */
  eventKey: string;
  values?: Record<string, string>;
  detail: string | null;
  timestamp: string | null;
}

interface AuditSectionProps {
  so: SalesOrder;
}

export const AuditSection = ({ so }: AuditSectionProps) => {
  const { t } = useTranslation(['orders', 'common']);
  const { token } = theme.useToken();

  // One release sentence for the live release AND every voided one — each
  // quotes the reasons and the floor ITS hold carried, never today's.
  const releaseDetail = (
    approval: SalesOrderPriceApproval,
    reasons: SalesOrderPriceHoldReason[],
    floorPct: number | null
  ): string =>
    `${t('orders:so.detail.audit.releasedBy')}: ${approval.approvedBy} — ${approval.reason}${
      reasons.length
        ? ` (${reasons
            .map((reason) =>
              t(holdReasonKey(reason, true), {
                pct: SO_PRICE_DEVIATION_THRESHOLD_PCT,
                floor: formatPct(floorPct),
              }).replace(/[.。]$/, '')
            )
            .join('; ')})`
        : ''
    }`;

  // A Modify re-prices and voids the live release; the voided release stays
  // on the record as its own pair of rows (released → voided), 2026-09-16.
  const voidedRows: AuditRow[] = (so.priceApprovalHistory ?? []).flatMap((record, index) => [
    {
      key: `price-release-${index}`,
      eventKey: 'orders:so.detail.audit.priceHoldReleased',
      detail: releaseDetail(record, record.holdReasons, record.marginFloorPct),
      timestamp: record.approvedAt,
    },
    {
      key: `price-release-voided-${index}`,
      eventKey: 'orders:so.detail.audit.priceHoldReleaseVoided',
      detail: null,
      timestamp: record.voidedAt,
    },
  ]);

  const rows: AuditRow[] = [
    {
      key: 'created',
      eventKey: 'orders:so.detail.audit.created',
      detail: null,
      timestamp: so.createdAt,
    },
    ...(so.updatedAt !== so.createdAt
      ? [
          {
            key: 'updated',
            eventKey: 'orders:so.detail.audit.updated',
            detail: null,
            timestamp: so.updatedAt,
          },
        ]
      : []),
    ...voidedRows,
    ...(so.priceApproval
      ? [
          {
            key: 'price-release',
            eventKey: 'orders:so.detail.audit.priceHoldReleased',
            detail: releaseDetail(so.priceApproval, so.priceHoldReasons, so.marginFloorPct),
            timestamp: so.priceApproval.approvedAt,
          },
        ]
      : []),
    ...so.lines
      .filter((line) => line.overrideReason)
      .map((line) => ({
        key: `override-${line.id}`,
        eventKey: 'orders:so.detail.audit.overrideFact',
        values: { sku: line.sku },
        detail: line.overrideReason ?? null,
        timestamp: null,
      })),
  ].sort((a, b) => (b.timestamp ?? '').localeCompare(a.timestamp ?? ''));

  const columns: TableProps<AuditRow>['columns'] = [
    {
      title: t('orders:so.detail.audit.event'),
      dataIndex: 'eventKey',
      key: 'event',
      width: 220,
      render: (eventKey: string, row) => t(eventKey, row.values),
    },
    {
      title: t('orders:so.detail.audit.detail'),
      dataIndex: 'detail',
      key: 'detail',
      render: (detail: string | null) => detail ?? renderDash(),
    },
    {
      title: t('orders:so.detail.audit.timestamp'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 220,
      render: (timestamp: string | null) =>
        timestamp ? <TimestampDisplay date={timestamp} showLabel={false} /> : renderDash(),
    },
  ];

  return (
    <div>
      <SectionHeader title={t('orders:so.detail.sections.audit')} />
      <TableContainer>
        <Table<AuditRow>
          rowKey="key"
          size="small"
          dataSource={rows}
          columns={columns}
          pagination={false}
        />
      </TableContainer>
      <Typography.Text
        type="secondary"
        style={{ display: 'block', marginTop: token.marginSM, fontSize: token.fontSizeSM }}
      >
        {t('orders:so.detail.audit.note')}
      </Typography.Text>
    </div>
  );
};

export default AuditSection;
